import { API, Auth } from "aws-amplify"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

const fetchOrganizationPurchaseOrders = async organizationId => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.get(
    "CORE",
    `/purchase_orders/${organizationId}`,
    payload,
  )
  return res
}

export const useOrganizationPurchaseOrders = (
  organizationId,
  enabled = false,
) => {
  return useQuery({
    queryKey: ["organizationPurchaseOrders", organizationId],
    queryFn: async () => {
      const res = await fetchOrganizationPurchaseOrders(organizationId)
      if (Array.isArray(res)) {
        return res.filter(row => !row._deleted_at)
      }
      if (res && typeof res === "object" && !res._deleted_at) {
        return [res]
      }
      return []
    },
    enabled,
  })
}

const fetchProjectPurchaseOrders = async (organizationId, projectId) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.get(
    "CORE",
    `/purchase_orders/${organizationId}/project/${projectId}`,
    payload,
  )
  return res
}

export const useProjectPurchaseOrders = (
  organizationId,
  projectId,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["projectPurchaseOrders", organizationId, projectId],
    queryFn: async () => {
      const res = await fetchProjectPurchaseOrders(organizationId, projectId)
      if (Array.isArray(res)) {
        return res.filter(row => !row._deleted_at)
      }
      if (res && typeof res === "object" && !res._deleted_at) {
        return [res]
      }
      return []
    },
    enabled: !!organizationId && !!projectId && enabled,
  })
}

const fetchUpdatePurchaseOrderPaymentStatus = async (
  organizationId,
  purchaseOrderId,
  paymentStatus,
) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.patch(
    "CORE",
    `/purchase_orders/payment-status/${organizationId}/${purchaseOrderId}?payment_status=${paymentStatus}`,
    payload,
  )
  return res
}

const fetchSinglePurchaseOrder = async (organizationId, purchaseOrderId) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const res = await API.get(
    "CORE",
    `/purchase_orders/${organizationId}/${purchaseOrderId}`,
    payload,
  )
  return res
}

export const useSinglePurchaseOrder = (
  organizationId,
  purchaseOrderId,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["purchaseOrder", organizationId, purchaseOrderId],
    queryFn: () => fetchSinglePurchaseOrder(organizationId, purchaseOrderId),
    enabled:
      !!organizationId &&
      !!purchaseOrderId &&
      purchaseOrderId !== "new" &&
      enabled,
  })
}

const fetchCreatePurchaseOrder = async (organizationId, purchaseOrder) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: purchaseOrder,
  }
  return API.post(
    "CORE",
    `/purchase_orders/?organizationId=${organizationId}`,
    payload,
  )
}

export const useCreatePurchaseOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, purchaseOrder }) =>
      fetchCreatePurchaseOrder(organizationId, purchaseOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organizationPurchaseOrders"],
      })
      queryClient.invalidateQueries({ queryKey: ["projectPurchaseOrders"] })
      queryClient.invalidateQueries({ queryKey: ["series"] })
    },
  })
}

const fetchEditPurchaseOrder = async (organizationId, purchaseOrder) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: purchaseOrder,
  }
  return API.put(
    "CORE",
    `/purchase_orders/?organizationId=${organizationId}`,
    payload,
  )
}

export const useEditPurchaseOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, purchaseOrder }) =>
      fetchEditPurchaseOrder(organizationId, purchaseOrder),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["organizationPurchaseOrders"],
      })
      queryClient.invalidateQueries({ queryKey: ["projectPurchaseOrders"] })
      if (variables?.purchaseOrder?.purchaseOrderId) {
        queryClient.invalidateQueries({
          queryKey: [
            "purchaseOrder",
            variables.organizationId,
            variables.purchaseOrder.purchaseOrderId,
          ],
        })
      }
    },
  })
}

export const useUpdatePurchaseOrderPaymentStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, purchaseOrder }) =>
      fetchUpdatePurchaseOrderPaymentStatus(
        organizationId,
        purchaseOrder.purchaseOrderId,
        purchaseOrder.paymentStatus,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["organizationPurchaseOrders"],
      })
      queryClient.invalidateQueries({ queryKey: ["projectPurchaseOrders"] })
      if (variables?.purchaseOrder?.purchaseOrderId) {
        queryClient.invalidateQueries({
          queryKey: [
            "purchaseOrder",
            variables.organizationId,
            variables.purchaseOrder.purchaseOrderId,
          ],
        })
      }
    },
  })
}

const fetchDeletePurchaseOrder = async (organizationId, purchaseOrderId) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  return API.del(
    "CORE",
    `/purchase_orders/${organizationId}/${purchaseOrderId}`,
    payload,
  )
}

export const useDeletePurchaseOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, purchaseOrderId }) =>
      fetchDeletePurchaseOrder(organizationId, purchaseOrderId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["organizationPurchaseOrders"],
      })
      queryClient.invalidateQueries({ queryKey: ["projectPurchaseOrders"] })
      if (variables?.purchaseOrderId) {
        queryClient.invalidateQueries({
          queryKey: [
            "purchaseOrder",
            variables.organizationId,
            variables.purchaseOrderId,
          ],
        })
      }
    },
  })
}

const fetchConvertPurchaseOrderToExpense = async (
  organizationId,
  purchaseOrderId,
) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: {},
  }
  return API.post(
    "CORE",
    `/purchase_orders/${organizationId}/${purchaseOrderId}/convert_to_expense`,
    payload,
  )
}

export const useConvertPurchaseOrderToExpense = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ organizationId, purchaseOrderId }) =>
      fetchConvertPurchaseOrderToExpense(organizationId, purchaseOrderId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["organizationPurchaseOrders"],
      })
      queryClient.invalidateQueries({ queryKey: ["projectPurchaseOrders"] })
      if (variables?.purchaseOrderId) {
        queryClient.invalidateQueries({
          queryKey: [
            "purchaseOrder",
            variables.organizationId,
            variables.purchaseOrderId,
          ],
        })
      }
    },
  })
}
